'use client'

import Link from 'next/link'
import s from './FeaturedStory.module.scss'
import { useEffect, useRef, useState, useCallback } from 'react'
import LazyImage from '@/components/Common/LazyImage'
import { AnimatePresence, motion } from 'framer-motion'

type Img = {
  imageUrl: string
  filename?: string
  metadata?: { dimensions?: { width?: number; height?: number } }
}

type Data = {
  _type: 'product' | 'story' | string
  title: string
  slug?: string
  client?: { title?: string }
  featuredImage?: Img
  featuredImageList?: Img[]
}

type Props = { data: Data; vertical: boolean; priority?: boolean }

const CYCLE_MS = 1000 // velocidad del pase de diapositivas

export default function FeaturedStory({ data, vertical, priority = false }: Props) {
  const containerRef = useRef<HTMLDivElement>(null)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const featured = data?.featuredImage || null
  const list = (data?.featuredImageList || []).filter(Boolean)
  const hasList = list.length > 0
  const baseImg: Img | null = featured ?? (list[0] ?? null)

  // estado
  const [showFeatured, setShowFeatured] = useState(true)
  const [idx, setIdx] = useState(0)
  const [current, setCurrent] = useState<Img | null>(baseImg)

  // capa previa para crossfade
  const prevRef = useRef<Img | null>(baseImg)
  useEffect(() => { prevRef.current = current }, [current])

  // destino del link
  const href =
    data._type === 'product' ? `/work/${data.slug ?? ''}` :
    data._type === 'story'   ? `/stories/${data.slug ?? ''}` : '#'

  // precarga
  useEffect(() => {
    if (!hasList) return
    list.forEach(im => { const i = new Image(); i.src = im.imageUrl })
  }, [hasList, list])

  // al volver a featured, fija imagen base
  useEffect(() => {
    if (showFeatured) setCurrent(baseImg)
  }, [showFeatured, baseImg])

  // utils timers
  const clearHoverTimer = () => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current)
      hoverTimerRef.current = null
    }
  }

  const stopCycle = useCallback(() => {
    clearHoverTimer()
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    setIdx(0)
    setShowFeatured(true)
  }, [])

  useEffect(() => () => stopCycle(), [stopCycle])

  // cooldown tras scroll (definido globalmente en StoriesComponent)
  const canStartNow = () => {
    const until = (window as any).__scrollCooldownUntil as number | undefined
    return !until || performance.now() >= until
  }

  // arranque con intent + respeto de cooldown
  const onPointerEnter = () => {
    clearHoverTimer()
    const tryStart = () => {
      if (!hasList) return
      if (!canStartNow()) {
        hoverTimerRef.current = setTimeout(tryStart, 120)
        return
      }
      if (!timerRef.current) {
        setShowFeatured(false)
        timerRef.current = setInterval(() => {
          setIdx(i => {
            const next = (i + 1) % list.length
            setCurrent(list[next] ?? baseImg)
            return next
          })
        }, CYCLE_MS)
      }
    }
    // pequeño intent para evitar parpadeos al pasar rápido
    hoverTimerRef.current = setTimeout(tryStart, 80)
  }

  const onPointerLeave = () => {
    stopCycle()
  }

  return (
    <div
      ref={containerRef}
      className={s.featuredProduct}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
    >
      <Link href={href} className={s.card}>
        <div className={`${s.imageWrapper} ${vertical ? s.portrait : s.landscape}`}>
          {/* capa previa */}
          {prevRef.current && (
            <figure className={`${s.figure} ${s.prev} ${s.active}`}>
              <LazyImage
                src={prevRef.current.imageUrl}
                alt={prevRef.current.filename || 'Previous'}
                fill
                objectFit="cover"
                width={prevRef.current.metadata?.dimensions?.width || 600}
                height={prevRef.current.metadata?.dimensions?.height || 450}
                priority={priority}
              />
            </figure>
          )}
          <AnimatePresence mode="sync">
            {prevRef.current && (
              <motion.figure
                key={prevRef.current.imageUrl}
                className={`${s.figure} ${s.prev} ${s.active}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
              >
                <LazyImage
                  src={prevRef.current.imageUrl}
                  alt={prevRef.current.filename || 'Previous'}
                  fill
                  objectFit="cover"
                  width={prevRef.current.metadata?.dimensions?.width || 600}
                  height={prevRef.current.metadata?.dimensions?.height || 450}
                  priority={priority}
                />
              </motion.figure>
            )}
          </AnimatePresence>

          {/* capa actual */}
          <AnimatePresence mode="sync">
            {current && (
              <motion.figure
                key={current.imageUrl}
                className={s.figure}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
              >
                <LazyImage
                  src={current.imageUrl}
                  alt={current.filename || "Featured Image"}
                  width={current.metadata?.dimensions?.width || 600}
                  height={current.metadata?.dimensions?.height || 450}
                  fill
                  objectFit="cover"
                  priority={priority}
                />
              </motion.figure>
            )}
          </AnimatePresence>
        </div>

        <div className={s.title}>
          <h3>
            {data.title}
            {data.client?.title && <span translate='no'> for {data.client.title}</span>}
          </h3>
        </div>
      </Link>
    </div>
  )
}